import { useEffect } from 'react';

/**
 * Applies a theme preset by setting `data-theme` on the document root
 * (docs/THEMING.md). CSS custom-property overrides in index.css are scoped to
 * `[data-theme="<preset>"]`, so changing the attribute switches the theme.
 * `null` / `undefined` / `'default'` mean the default preset (no attribute).
 * (`useThemePreset` / `useTheme` read it back.) On unmount, or a preset
 * change, the attribute is restored to whatever it was before this hook set it.
 *
 * Used by `useOrgTheme` (OrgLayout) and the public survey page, which has no
 * org context and gets the preset from the public API instead.
 */
export function useApplyThemePreset(preset: string | null | undefined) {
  const resolved = preset || 'default';

  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('data-theme');
    if (resolved === 'default') {
      root.removeAttribute('data-theme');
    } else {
      root.setAttribute('data-theme', resolved);
    }

    return () => {
      if (previous === null) root.removeAttribute('data-theme');
      else root.setAttribute('data-theme', previous);
    };
  }, [resolved]);
}
